import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Observable, BehaviorSubject, of, combineLatest} from 'rxjs';
import {DataService} from '../data.service';
import {DownloadFileResponse} from 'src/app/shared/common-components/file-storage/downloadFileResponse';
import {StateCountryService} from '../state-country/state-country.service';
import {shareReplay, map, switchMap, catchError, tap, mergeMap, filter} from 'rxjs/operators';
import {Carrier, RatesTspScope} from 'src/app/shared/common-components/common-structure';
import {TransportType} from 'src/app/shared/common-components/AppConstants';
import {LoadingService} from '../loading/loading.service';
import {AlertService} from '../alert/alert.service';

@Injectable({
  providedIn: 'root'
})
export class HhgScopeService extends DataService {

  private baseURI = this.apiUri + '/ratescope/';
  public observable$: any;

  private carrierId = new BehaviorSubject<number>(0);

  private modifiedInterstateScopes = new BehaviorSubject<RatesTspScope[]>([]);
  private deletedInterstateScopes = new BehaviorSubject<number[]>([]);
  private modifiedInternationalScopes = new BehaviorSubject<RatesTspScope[]>([]);
  private deletedInternationalScopes = new BehaviorSubject<number[]>([]);


  stateCountries$ = this.stateCountryService.getAll().pipe(shareReplay(1));
  states$ = this.stateCountries$.pipe(map((results) => results.filter(state => state.stateCountryType === 'State')), shareReplay(1));
  serviceAreas$ = this.stateCountries$.pipe(map((results) => results.filter(state => state.stateCountryType === 'Service Area' && state.stateCountryCode !== '0000'))
    , shareReplay(1));
  countries$ = this.stateCountries$.pipe(map((results) => results.filter(state => state.stateCountryType === 'Country')), shareReplay(1));

  carrierScopes$ = combineLatest([this.carrierId, this.stateCountries$]).pipe(filter(([c, s]) => c > 0)).pipe(
    tap(t => this.loader.show()),
    switchMap(([carrierId, states]) => this.getCarrierScope(carrierId).pipe(
      tap(t => this.loader.hide(),
        catchError(e => {
          this.loader.hide();
          return of([] as RatesTspScope[]);
        })
      )
    )),
    shareReplay(1)
  );

  intrastateCarrierScopes$ = this.carrierScopes$.pipe(map((results: any) => results.filter(scope => scope.scopeType === 'Intrastate')));
  interstateCarrierScopes$ = combineLatest([
    this.carrierScopes$.pipe(map((results: any) => results.filter(scope => scope.scopeType === 'Interstate'))),
    this.modifiedInterstateScopes, this.deletedInterstateScopes
  ]).pipe(map(([savedInterstateScopes , addedInterstateScopes, deletedInterstateScopes]) => {
    const updatedInterstateScopes = [...savedInterstateScopes.filter(s => !deletedInterstateScopes.find(d => d === s.scopeId))];
    return [...updatedInterstateScopes, ...addedInterstateScopes];
  }));
  internationalCarrierScopes$ = combineLatest([
    this.carrierScopes$.pipe(map((results: any) => results.filter(scope => scope.scopeType === 'International'))),
    this.modifiedInternationalScopes, this.deletedInternationalScopes
  ]).pipe(map(([savedInternationalScopes , addedInternationalScopes, deletedInternationalScopes]) => {
    const updatedInternationalScopes = [...savedInternationalScopes.filter(s => !deletedInternationalScopes.find(d => d === s.scopeId))];
    return [...updatedInternationalScopes, ...addedInternationalScopes];
  }));

  constructor(http: HttpClient, private stateCountryService: StateCountryService,
              private loader: LoadingService, private alertService: AlertService) {
    super(http);
  }

  getCarrier(carrierId: number): Observable<any> {
    return this.http.get(this.apiUri + '/carrier/' + carrierId + '/carrierid/carrieronly');
  }

  getAllCarriers(): Observable<any> {
    return this.http.get(this.apiUri + '/smallHHGCarrier');
  }

  // Purpose of this function is to get List of all HHG Carriers (Small version of Carrier Entity)
  getAllSmallCarrierListByBusinessTypeAndStatus(): Observable<any> {
    return this.http.get(this.apiUri + '/smallHHGCarrier/scacList');
  }

// below function gets carrier small version for both HHG & FRT business.
  getBothCarrriers(): Observable<any> {
    return this.http.get(this.apiUri + '/smallCarrier/allScacList');
  }

// below function gets carrier small version for FRT business.
  getFRTCarriers(): Observable<any> {
    return this.http.get(this.apiUri + '/smallFRTCarrier/scacList');
  }

  updateCarrierId(carrierId: number) {
    this.carrierId.next(carrierId);
  }

  getCarrierScope(carrierId: number): Observable<RatesTspScope[]> {
    const uri = this.baseURI + carrierId + '/carrierId';
    return this.http.get<RatesTspScope[]>(uri);
  }

  modifiedCarrierScopes(scopes: RatesTspScope[], scopeType: TransportType) {
    scopeType === TransportType.INTERNATIONAL ? this.modifiedInternationalScopes.next([...scopes])
      : this.modifiedInterstateScopes.next([...scopes]);
  }

  deletedCarrierScopes(scopeIds: number[], scopeType: TransportType) {
    scopeType === TransportType.INTERNATIONAL ? this.deletedInternationalScopes.next([...this.deletedInternationalScopes.value, ...scopeIds])
      : this.deletedInterstateScopes.next([...this.deletedInterstateScopes.value, ...scopeIds]);
  }

  saveIntraStateScopes(modifiedScopes: RatesTspScope[], deletedScopes: number[], carrier: Carrier) {
    const addCarrierScopes = this.addCarrierScope(modifiedScopes);
    const deletedCarrierScopes = this.removeCarrierScope(deletedScopes);
    this.loader.show();
    return deletedCarrierScopes.pipe(
      mergeMap(s => addCarrierScopes.pipe(tap(
        () => {
          this.loader.hide();
          this.alertService.success(carrier.scac + '-' + carrier.scacName + '<br/>' + 'Successfully updated ' +
            ((+modifiedScopes.length) + (+deletedScopes.length))
            + ' intrastate scopes');
        },
        err => {
          this.loader.hide();
          this.alertService.error(carrier.scac + '-' + carrier.scacName + '<br/>' + 'Error in updating ' +
            ((+modifiedScopes.length) + (+deletedScopes.length))
            + ' intrastate scopes');
        }
      ))),
      catchError(err => {
        this.loader.hide();
        this.alertService.error(carrier.scac + '-' + carrier.scacName + '<br/>' + 'Error in updating ' +
          ((+modifiedScopes.length) + (+deletedScopes.length))
          + ' intrastate scopes');
        return of([]);
      })
    );
  }

  saveInterStateScopes(carrier: Carrier) {
    const addCarrierScopes = this.addCarrierScope(this.modifiedInterstateScopes.value);
    const deletedCarrierScopes = this.removeCarrierScope(this.deletedInterstateScopes.value);
    this.loader.show();
    return deletedCarrierScopes.pipe(
      mergeMap(s => addCarrierScopes.pipe(tap(
        () => {
          this.loader.hide();
          this.alertService.success(carrier.scac + '-' + carrier.scacName + '<br/>' + 'Successfully updated ' +
            ((+this.modifiedInterstateScopes.value.length) + (+this.deletedInterstateScopes.value.length))
            + ' interstate scopes');
          this.modifiedInterstateScopes.next([]);
          this.deletedInterstateScopes.next([]);
        },
        err => {
          this.loader.hide();
          this.alertService.error(carrier.scac + '-' + carrier.scacName + '<br/>' + 'Error in updating ' +
            (+this.modifiedInterstateScopes.value.length) + (+this.deletedInterstateScopes.value.length)
            + ' interstate scopes');
        }
      ))),
      catchError(err => {
        this.loader.hide();
        this.alertService.error(carrier.scac + '-' + carrier.scacName + '<br/>' + 'Error in updating ' +
          (+this.modifiedInterstateScopes.value.length) + (+this.deletedInterstateScopes.value.length)
          + ' interstate scopes');
        return of([]);
      })
    );
  }

  saveInternationalScopes(carrier: Carrier) {
    const addCarrierScopes = this.addCarrierScope(this.modifiedInternationalScopes.value);
    const deletedCarrierScopes = this.removeCarrierScope(this.deletedInternationalScopes.value);
    this.loader.show();
    return deletedCarrierScopes.pipe(
      mergeMap(s => addCarrierScopes.pipe(tap(
        () => {
          this.loader.hide();
          this.alertService.success(carrier.scac + '-' + carrier.scacName + '<br/>' + 'Successfully updated ' +
            ((+this.modifiedInternationalScopes.value.length) + (+this.deletedInternationalScopes.value.length))
            + ' international scopes');
          this.modifiedInternationalScopes.next([]);
          this.deletedInternationalScopes.next([]);
        },
        err => {
          this.loader.hide();
          this.alertService.error(carrier.scac + '-' + carrier.scacName + '<br/>' + 'Error in updating ' +
            (+this.modifiedInternationalScopes.value.length) + (+this.deletedInternationalScopes.value.length)
            + ' international scopes');
        }
      ))),
      catchError(err => {
        this.loader.hide();
        this.alertService.error(carrier.scac + '-' + carrier.scacName + '<br/>' + 'Error in updating ' +
          (+this.modifiedInternationalScopes.value.length) + (+this.deletedInternationalScopes.value.length)
          + ' international scopes');
        return of([]);
      })
    );
  }

  addCarrierScope(rates: RatesTspScope[]): Observable<any> {
    return rates && rates.length > 0 ? this.http.post(this.baseURI + 'saveRatesScopes', rates) : of([]);
  }

  removeCarrierScope(scopeIds: number[]): Observable<any> {
    return scopeIds && scopeIds.length > 0 ? this.http.put(this.baseURI + 'deleteRatesScopesByIDs', scopeIds) : of([]);
  }

  removeCarrierScope2(scope: number): Observable<any> {
    return this.http.post(this.baseURI + scope + '/delete', scope);
  }

  removeCarrierScope3(scope: number): Observable<any> {
    return this.http.delete(this.baseURI + scope, {responseType: 'text'});
  }

  getAllStateCountries(): Observable<any> {
    // todo - remove public
    return this.http.get(this.apiUri + '/statecountry/');
  }

  getStates(): Observable<any> {
    return this.http.get(this.apiUri + '/statecountry/State/statecountrytype');
  }

  getCountries(): Observable<any> {
    return this.http.get(this.apiUri + '/statecountry/Country/statecountrytype');
  }

  getServiceAreas(): Observable<any> {
    return this.http.get(this.apiUri + '/statecountry/Service%20Areas/statecountrytype');
  }

  // call to server for Scope list report
  getScopeListXlsx(carrierId: number, scopeType: string): Observable<DownloadFileResponse> {
    this.setUri(this.baseURI + 'scopeListXlsx/' + carrierId + '/carrierId/' + scopeType + '/scopeType');
    return this.get().pipe(catchError(this.handleError));
  }

  // call to server to get the scope report for all Carriers.
  getHhgScopeReport(carrierIdList: any, scopeType: string, originId: any, destinationId: any): Observable<DownloadFileResponse> {
    this.setUri(this.baseURI + 'hhgScopeReportXlsx/' + carrierIdList + '/carrierIdList/' + scopeType + '/scopeType/' + originId + '/originId/' + destinationId + '/destinationId');
    return this.get().pipe(catchError(this.handleError));
  }

  getAllCarrierListByBusinessTypeAndStatus(businessTypeList: any, status: string): Observable<any> {
    this.setUri(this.baseURI + 'scacList/' + businessTypeList + '/businessTypeList/' + status + '/status');
    return this.get().pipe(catchError(this.handleError));
  }

  getHhgScopeReportData(carrierId, scopeType, originId, destinationId) {
    this.setUri(this.baseURI + 'getHhgScopes/' + carrierId + '/carrierIdList/' + scopeType + '/scopeType/' + originId + '/originId/' + destinationId + '/destinationId');
    return this.get().pipe(catchError(this.handleError));
  }

  getAllScopes(carrierId: number, scopeType: string): Observable<any> {
    return this.http.get(this.baseURI +  carrierId + '/carrierId/' + scopeType + '/allScope');
  }

  getAuditTrailListByCarIDAndScopeType(carrierId: number, scopeType: string): Observable<any> {
    const auditTrailListUrl = this.apiUri+ '/ratescope/' + carrierId + '/carrierId/' + scopeType + '/scopeType/scopeAuditTrail';
    console.log('shipmentId in audittrail : ' + carrierId + 'scopeType' +scopeType);
    if (carrierId !== undefined && scopeType !== undefined) {
      this.setUri(auditTrailListUrl);
      return this.get();
    }
  }
}
